import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { AppRoutes } from 'AppRoutes';
import * as qs from 'query-string';

import type { ResetPasswordRequest } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderNoAuth/models';
import { PASSWORD_MIN_LENGTH } from '@provider-portal/common/utils/passwordValidator';

import { Island, Typography } from '@bolteu/clay-react';

import {
  requestRequestResetPasswordAsyncAction,
  requestSetNewPasswordAsyncAction,
  selectPasswordRequestState,
  selectPasswordValidationErrors,
} from 'rdx/password';
import { $tk } from 'utils/appTranslationKey';

import { ForgotPasswordForm } from 'components/ForgotPassword/ForgotPasswordForm';
import { SetNewPasswordForm } from 'components/ForgotPassword/SetNewPasswordForm';

import { useLocalizedDocumentTitle } from 'root/navigation/useLocalizedDocumentTitle';

const queryStringObject = qs.parse(window.location.search);
const resetPasswordToken = queryStringObject?.token;

export const ForgotPassword = () => {
  useLocalizedDocumentTitle($tk.screens.ForgotPassword.title);

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const [passwordChangeMessage, setPasswordChangeMessage] = useState<string | null>(null);
  const [requestPasswordMessage, setRequestPasswordMessage] = useState<string | null>(null);

  const requestState = useSelector(selectPasswordRequestState);
  const passwordRequestValidationErrors = useSelector(selectPasswordValidationErrors);

  const strings = {
    setNewPasswordForm: {
      passwordNotMatched: intl.formatMessage({
        id: $tk.screens.ForgotPassword.password_not_matched,
      }),
      passwordMinimumRequirements: intl.formatMessage(
        { id: $tk.screens.ForgotPassword.password_minimum_requirements_with_dynamic_value },
        { value: PASSWORD_MIN_LENGTH },
      ),
      passwordChanged: intl.formatMessage({ id: $tk.screens.ForgotPassword.password_changed }),
      error: intl.formatMessage({
        id: $tk.screens.ForgotPassword.problemWhileTryingToResetPassword,
      }),
      password: intl.formatMessage({ id: $tk.screens.ForgotPassword.password }),
      confirmPassword: intl.formatMessage({ id: $tk.screens.ForgotPassword.confirm_password }),
      setPassword: intl.formatMessage({ id: $tk.screens.ForgotPassword.set_password }),
      goToLogin: intl.formatMessage({ id: $tk.screens.ForgotPassword.got_to_login }),
    },
    forgotPasswordForm: {
      error: intl.formatMessage({
        id: $tk.screens.ForgotPassword.problemWhileTryingToResetPassword,
      }),
      emailSend: intl.formatMessage({ id: $tk.screens.ForgotPassword.email_send }),
      username: intl.formatMessage({ id: $tk.screens.ForgotPassword.username }),
      resetPassword: intl.formatMessage({ id: $tk.screens.ForgotPassword.reset_password }),
    },
  };

  const onNewPasswordSubmitHandler = (payload: ResetPasswordRequest) => {
    dispatch(requestSetNewPasswordAsyncAction.request(payload));
  };

  const onResetPasswordHandler = (username: string) => {
    dispatch(requestRequestResetPasswordAsyncAction.request({ username }));
  };

  useEffect(() => {
    if (passwordRequestValidationErrors && passwordRequestValidationErrors.password) {
      setPasswordChangeMessage(passwordRequestValidationErrors.password.join('<br/>'));
      return;
    }

    if (passwordRequestValidationErrors && passwordRequestValidationErrors.email) {
      setRequestPasswordMessage(passwordRequestValidationErrors.email.join('<br/>'));
      return;
    }

    setRequestPasswordMessage(null);
    setPasswordChangeMessage(null);
  }, [passwordRequestValidationErrors]);

  const onGoBackToLoginHandler = () => {
    history.push(AppRoutes.AUTH);
  };

  return (
    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <div className="w-[85vw] max-w-[450px]">
        <Island elevation="floor-2" className="px-6 py-8">
          <Typography defaultTag="h6" className="bolt-font-heading-xs-accent">
            {!resetPasswordToken
              ? intl.formatMessage({ id: $tk.screens.ForgotPassword.title })
              : intl.formatMessage({ id: $tk.screens.ForgotPassword.set_password_title })}
          </Typography>
          {!resetPasswordToken ? (
            <Typography>
              {intl.formatMessage({ id: $tk.screens.ForgotPassword.description_password_reset })}
            </Typography>
          ) : null}
          {!resetPasswordToken ? (
            <ForgotPasswordForm
              strings={strings.forgotPasswordForm}
              onResetPassword={onResetPasswordHandler}
              alertMessage={requestPasswordMessage}
              requestState={requestState}
            />
          ) : (
            <SetNewPasswordForm
              token={resetPasswordToken.toString()}
              strings={strings.setNewPasswordForm}
              onSubmit={onNewPasswordSubmitHandler}
              onGoBackToLogin={onGoBackToLoginHandler}
              alertMessage={passwordChangeMessage}
              requestState={requestState}
            />
          )}
        </Island>
      </div>
    </div>
  );
};
